import { create } from 'zustand';
import { getChatbotConfigSection, updateChatbotConfigSection } from '../utils/chatbotConfigAccess';
import { Language, MultilingualSettings, TranslatedContent } from '../types/multilingualSettings';

const DEFAULT_SETTINGS: MultilingualSettings = {
  enabled: false,
  languages: [
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' }
  ],
  activeLanguages: ['en', 'fr'],
  defaultLanguage: 'en',
  currentLanguage: 'en',
  translations: {}
};

interface MultilingualStore {
  settings: MultilingualSettings;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  updateSettings: (settings: Partial<MultilingualSettings>, chatbotPublicId: string) => Promise<void>;
  toggleLanguage: (code: string, chatbotPublicId: string) => Promise<void>;
  setDefaultLanguage: (code: string, chatbotPublicId: string) => Promise<void>;
  addLanguage: (language: Language, chatbotPublicId: string) => Promise<void>;
  removeLanguage: (code: string, chatbotPublicId: string) => Promise<void>;
  initializeSettings: (chatbotPublicId: string) => Promise<void>;
  setCurrentLanguage: (code: string, chatbotPublicId: string) => Promise<void>;
}

export const useMultilingualSettings = create<MultilingualStore>((set, get) => ({
  settings: DEFAULT_SETTINGS,
  loading: false,
  error: null,
  initialized: false,

  initializeSettings: async (chatbotPublicId) => {
    try {
      set({ loading: true, error: null });

      // Get multilingual settings from the centralized config
      const multilingualSettings = await getChatbotConfigSection<MultilingualSettings>(
        'multilingual_settings',
        chatbotPublicId
      );

      if (multilingualSettings) {
        // Ensure currentLanguage is set, defaulting to defaultLanguage if not present
        const finalSettings = {
          ...DEFAULT_SETTINGS,
          ...multilingualSettings,
          // Assurez-vous que currentLanguage est défini, sinon utilisez defaultLanguage
          currentLanguage: multilingualSettings.currentLanguage || multilingualSettings.defaultLanguage
        };
        
        set({ 
          settings: finalSettings,
          initialized: true,
          loading: false
        });
      } else {
        // Use default settings if none are found
        set({ 
          settings: {
            ...DEFAULT_SETTINGS,
          },
          initialized: true,
          loading: false
        });
      }
    } catch (err) {
      set({ 
        error: err instanceof Error ? err.message : 'Error loading multilingual settings',
        settings: DEFAULT_SETTINGS,
        initialized: true,
        loading: false
      });
    }
  },

  updateSettings: async (newSettings, chatbotPublicId) => {
    try {
      set({ loading: true, error: null });
      
      const currentSettings = get().settings;
      const updatedSettings = {
        ...currentSettings,
        ...newSettings,
      };
      
      // Si le mode multilingue est activé/désactivé, ajuster currentLanguage
      if (newSettings.enabled !== undefined && currentSettings.enabled !== newSettings.enabled) {
        // Si on active le mode, s'assurer que currentLanguage est défini sur la langue par défaut
        if (newSettings.enabled) {
          // Toujours réinitialiser à la langue par défaut (typiquement 'en') lors de l'activation
          updatedSettings.currentLanguage = updatedSettings.defaultLanguage || 'en';
        }
      }

      const success = await updateChatbotConfigSection(
        'multilingual_settings',
        updatedSettings,
        chatbotPublicId
      );

      if (!success) {
        throw new Error('Failed to update multilingual settings');
      }

      set({ settings: updatedSettings });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Error updating settings' });
    } finally {
      set({ loading: false });
    }
  },

  toggleLanguage: async (code, chatbotPublicId) => {
    try {
      set({ loading: true, error: null });

      const { activeLanguages, defaultLanguage, currentLanguage } = get().settings;
      
      // Cannot deactivate default language
      if (code === defaultLanguage) {
        throw new Error('Cannot deactivate default language');
      }
      
      // Toggle the language in activeLanguages
      const isCurrentlyActive = activeLanguages.includes(code);
      const newActiveLanguages = isCurrentlyActive
        ? activeLanguages.filter(lang => lang !== code)
        : [...activeLanguages, code];
      
      // Si on désactive la langue courante, revenir à la langue par défaut
      let newCurrentLanguage = currentLanguage;
      if (isCurrentlyActive && code === currentLanguage) {
        newCurrentLanguage = defaultLanguage;
      }

      const updatedSettings = {
        ...get().settings,
        activeLanguages: newActiveLanguages,
        currentLanguage: newCurrentLanguage
      };

      const success = await updateChatbotConfigSection(
        'multilingual_settings',
        updatedSettings,
        chatbotPublicId
      );

      if (!success) {
        throw new Error('Failed to toggle language');
      }

      set({ settings: updatedSettings });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Error toggling language' });
    } finally {
      set({ loading: false });
    }
  },

  setDefaultLanguage: async (code, chatbotPublicId) => {
    try {
      set({ loading: true, error: null });

      const { activeLanguages } = get().settings;
      
      // Make sure the new default language is active
      const newActiveLanguages = activeLanguages.includes(code)
        ? activeLanguages
        : [...activeLanguages, code];

      const updatedSettings = {
        ...get().settings,
        defaultLanguage: code,
        activeLanguages: newActiveLanguages,
        currentLanguage: code
      };

      const success = await updateChatbotConfigSection(
        'multilingual_settings',
        updatedSettings,
        chatbotPublicId
      );

      if (!success) {
        throw new Error('Failed to set default language');
      }

      set({ settings: updatedSettings });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Error setting default language' });
    } finally {
      set({ loading: false });
    }
  },

  addLanguage: async (language, chatbotPublicId) => {
    try {
      set({ loading: true, error: null });

      const newLanguages = [...get().settings.languages, language];
      const updatedSettings = {
        ...get().settings,
        languages: newLanguages
      };

      const success = await updateChatbotConfigSection(
        'multilingual_settings',
        updatedSettings,
        chatbotPublicId
      );

      if (!success) {
        throw new Error('Failed to add language');
      }

      set({ settings: updatedSettings });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Error adding language' });
    } finally {
      set({ loading: false });
    }
  },

  removeLanguage: async (code, chatbotPublicId) => {
    try {
      set({ loading: true, error: null });

      const { defaultLanguage } = get().settings;
      if (code === defaultLanguage) {
        throw new Error('Cannot remove default language');
      }

      const newLanguages = get().settings.languages.filter(lang => lang.code !== code);
      const newActiveLanguages = get().settings.activeLanguages.filter(lang => lang !== code);
      
      const updatedSettings = {
        ...get().settings,
        languages: newLanguages,
        activeLanguages: newActiveLanguages
      };

      const success = await updateChatbotConfigSection(
        'multilingual_settings',
        updatedSettings,
        chatbotPublicId
      );

      if (!success) {
        throw new Error('Failed to remove language');
      }

      set({ settings: updatedSettings });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Error removing language' });
    } finally {
      set({ loading: false });
    }
  },

  setCurrentLanguage: async (code, chatbotPublicId) => {
    try {
      set({ loading: true, error: null });

      const updatedSettings = {
        ...get().settings,
        currentLanguage: code
      };

      const success = await updateChatbotConfigSection(
        'multilingual_settings',
        updatedSettings,
        chatbotPublicId
      );

      if (!success) {
        throw new Error('Failed to set current language');
      }

      set({ settings: updatedSettings });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Error setting current language' });
    } finally {
      set({ loading: false });
    }
  }
}));